import React, { useState, useCallback, useEffect, useRef } from 'react';
import { cn } from '../../utils/cn';
import {
  useAssetPreviewStore,
  PrefabGameObject,
} from '../../store/assetPreviewStore';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { readFile, writeFile } from '../../services/FileSystemService';
import { PrefabContextMenu } from './PrefabContextMenu';
import { useAssetUrl } from '../../hooks/useAssetUrl';
import { GraphicsSvgRenderer, GraphicsRendererData } from '../common/GraphicsSvgRenderer';
import { BUILT_IN_COMPONENTS, getComponentDefinition } from '../../constants/components';
import { PropertyDefinition } from '../../../core/types/inspector.types';
import {
  SliderInput,
  ColorPicker,
  TexturePropertyEditor,
} from '../inspector/PropertyEditors';
import {
  ChevronRight,
  ChevronDown,
  Plus,
  Trash2,
  Copy,
  Clipboard,
  Undo,
  Redo,
  Save,
  Box,
  Square,
  Circle,
  Type,
  ZoomIn,
  ZoomOut,
  Home,
} from 'lucide-react';

interface PrefabPreviewProps {
  instanceId: string;
}

interface TreeNodeProps {
  objectId: string;
  instanceId: string;
  depth: number;
  onContextMenu: (e: React.MouseEvent, objectId: string) => void;
  onStartRename: (objectId: string) => void;
  renamingId: string | null;
  onFinishRename: () => void;
}

const OBJECT_TYPE_ICONS: Record<string, React.FC<{ size?: number }>> = {
  empty: Box,
  sprite: Square,
  container: Box,
  text: Type,
  graphics: Circle,
};

// CSS colors for default object type placeholders
const OBJECT_TYPE_CSS_COLORS: Record<string, string> = {
  empty: '#6b7280',
  sprite: '#3b82f6',
  container: '#8b5cf6',
  text: '#10b981',
  graphics: '#f59e0b',
};

// ============================================================================
// Helper Components for Type-Specific Rendering
// ============================================================================

/** Renders a sprite texture image using useAssetUrl (mirrors SceneCanvas SpriteTextureImage) */
const PrefabSpriteTextureImage: React.FC<{
  texturePath: string | null;
  tint?: string;
  alpha?: number;
}> = ({ texturePath, tint, alpha }) => {
  const { url } = useAssetUrl(texturePath);
  if (!url) return null;
  const hasTint = tint && tint !== '#ffffff';
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ opacity: alpha ?? 1 }}>
      <img src={url} alt="" className="w-full h-full object-fill" draggable={false} />
      {hasTint && (
        <div
          className="absolute inset-0"
          style={{ backgroundColor: tint, mixBlendMode: 'multiply' }}
        />
      )}
    </div>
  );
};

/** Name label below an object in the scene preview */
const PrefabObjectLabel: React.FC<{ name: string; isSelected: boolean; rotation: number }> = ({
  name,
  isSelected,
  rotation,
}) => (
  <div
    className="absolute -bottom-5 left-1/2 whitespace-nowrap text-[10px] bg-zinc-800 px-1.5 py-0.5 rounded z-20 pointer-events-none"
    style={{
      color: isSelected ? '#ffffff' : '#a1a1aa',
      transform: `translateX(-50%) rotate(${-rotation}deg)`,
    }}
  >
    {name}
  </div>
);

// ============================================================================
// PrefabGameObjectRenderer — type-aware renderer (mirrors SceneCanvas)
// ============================================================================

interface PrefabGameObjectRendererProps {
  obj: PrefabGameObject;
  screenX: number;
  screenY: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  zoom: number;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
}

const PrefabGameObjectRenderer: React.FC<PrefabGameObjectRendererProps> = ({
  obj,
  screenX,
  screenY,
  rotation,
  scaleX,
  scaleY,
  zoom,
  isSelected,
  onClick,
}) => {
  const objType = obj.type || 'empty';
  const isRoot = obj.parentId === null;

  // Compute base size from component data
  let baseSizeW = 40;
  let baseSizeH = 40;

  if (objType === 'sprite') {
    const spriteComp = obj.components.find((c) => (c as { type?: string }).type === 'sprite-renderer');
    const data = (spriteComp as { data?: Record<string, unknown> })?.data;
    const tw = data?.textureWidth as number | undefined;
    const th = data?.textureHeight as number | undefined;
    if (tw && th && tw > 0 && th > 0) {
      baseSizeW = tw;
      baseSizeH = th;
    }
  }

  if (objType === 'text') {
    const textComp = obj.components.find((c) => (c as { type?: string }).type === 'text-renderer');
    const data = (textComp as { data?: Record<string, unknown> })?.data;
    const fontSize = (data?.fontSize as number) ?? 24;
    const textContent = (data?.text as string) ?? 'New Text';
    const wordWrap = (data?.wordWrap as boolean) ?? false;
    const wordWrapWidth = (data?.wordWrapWidth as number) ?? 200;
    if (wordWrap) {
      baseSizeW = wordWrapWidth;
      const estimatedLines = Math.max(1, Math.ceil((textContent.length * fontSize * 0.6) / wordWrapWidth));
      baseSizeH = Math.max(fontSize * 1.4, estimatedLines * fontSize * 1.4);
    } else {
      baseSizeW = Math.max(40, textContent.length * fontSize * 0.6);
      baseSizeH = fontSize * 1.4;
    }
  }

  if (objType === 'graphics') {
    const gfxComp = obj.components.find((c) => (c as { type?: string }).type === 'graphics-renderer');
    const data = (gfxComp as { data?: Record<string, unknown> })?.data;
    if (data) {
      const gShape = (data.shape as string) ?? 'rectangle';
      if (gShape === 'circle') {
        const r = (data.radius as number) ?? 50;
        baseSizeW = r * 2;
        baseSizeH = r * 2;
      } else {
        baseSizeW = (data.width as number) ?? 100;
        baseSizeH = (data.height as number) ?? 100;
      }
    }
  }

  const width = baseSizeW * Math.abs(scaleX) * zoom;
  const height = baseSizeH * Math.abs(scaleY) * zoom;

  const borderStyle = isSelected
    ? '2px solid rgba(59, 130, 246, 1)'
    : '1px solid transparent';

  // --- Sprite ---
  if (objType === 'sprite') {
    const spriteComp = obj.components.find((c) => (c as { type?: string }).type === 'sprite-renderer');
    const data = (spriteComp as { data?: Record<string, unknown> })?.data;
    const texturePath = (data?.texturePath as string | null) ?? null;
    const flipX = (data?.flipX as boolean) ?? false;
    const flipY = (data?.flipY as boolean) ?? false;
    const flipScaleX = flipX ? ' scaleX(-1)' : '';
    const flipScaleY = flipY ? ' scaleY(-1)' : '';

    return (
      <div
        className="absolute cursor-pointer select-none"
        style={{
          left: screenX - width / 2,
          top: screenY - height / 2,
          width,
          height,
          transform: `rotate(${rotation}deg)${flipScaleX}${flipScaleY}`,
          transformOrigin: 'center',
          border: borderStyle,
          boxSizing: 'border-box',
        }}
        onClick={onClick}
      >
        {texturePath ? (
          <PrefabSpriteTextureImage
            texturePath={texturePath}
            tint={data?.tint as string | undefined}
            alpha={data?.alpha as number | undefined}
          />
        ) : (
          <div
            className="absolute inset-0 rounded flex items-center justify-center"
            style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)' }}
          >
            <Square size={14} className="text-blue-400 opacity-60" />
          </div>
        )}
        <PrefabObjectLabel name={obj.name} isSelected={isSelected} rotation={rotation} />
      </div>
    );
  }

  // --- Text ---
  if (objType === 'text') {
    const textComp = obj.components.find((c) => (c as { type?: string }).type === 'text-renderer');
    const data = (textComp as { data?: Record<string, unknown> })?.data;
    const textContent = (data?.text as string) ?? 'New Text';
    const fontFamily = (data?.fontFamily as string) ?? 'Arial';
    const fontSize = (data?.fontSize as number) ?? 24;
    const fontWeight = (data?.fontWeight as string) ?? 'normal';
    const fontStyle = (data?.fontStyle as string) ?? 'normal';
    const fill = (data?.fill as string) ?? '#ffffff';
    const align = (data?.align as string) ?? 'left';
    const wordWrap = (data?.wordWrap as boolean) ?? false;
    const letterSpacing = (data?.letterSpacing as number) ?? 0;
    const lineHeight = (data?.lineHeight as number) || undefined;

    return (
      <div
        className="absolute cursor-pointer select-none"
        style={{
          left: screenX - width / 2,
          top: screenY - height / 2,
          width,
          height,
          transform: `rotate(${rotation}deg)`,
          transformOrigin: 'center',
          border: borderStyle,
          boxSizing: 'border-box',
        }}
        onClick={onClick}
      >
        <div
          className="absolute inset-0 overflow-hidden pointer-events-none"
          style={{
            fontFamily,
            fontSize: fontSize * zoom,
            fontWeight,
            fontStyle,
            color: fill,
            textAlign: align as React.CSSProperties['textAlign'],
            whiteSpace: wordWrap ? 'pre-wrap' : 'nowrap',
            letterSpacing: letterSpacing * zoom,
            lineHeight: lineHeight ? `${lineHeight * zoom}px` : undefined,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent:
              align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
          }}
        >
          {textContent}
        </div>
        <PrefabObjectLabel name={obj.name} isSelected={isSelected} rotation={rotation} />
      </div>
    );
  }

  // --- Graphics ---
  if (objType === 'graphics') {
    const gfxComp = obj.components.find((c) => (c as { type?: string }).type === 'graphics-renderer');
    const gfxData = ((gfxComp as { data?: Record<string, unknown> })?.data ?? {}) as GraphicsRendererData;

    return (
      <div
        className="absolute cursor-pointer select-none"
        style={{
          left: screenX - width / 2,
          top: screenY - height / 2,
          width,
          height,
          transform: `rotate(${rotation}deg)`,
          transformOrigin: 'center',
          border: borderStyle,
          boxSizing: 'border-box',
        }}
        onClick={onClick}
      >
        <GraphicsSvgRenderer data={gfxData} renderWidth={width} renderHeight={height} />
        <PrefabObjectLabel name={obj.name} isSelected={isSelected} rotation={rotation} />
      </div>
    );
  }

  // --- Default (empty / container / unknown) ---
  const color = isRoot ? '#22c55e' : (OBJECT_TYPE_CSS_COLORS[objType] || '#6b7280');
  const TypeIcon = OBJECT_TYPE_ICONS[objType] || OBJECT_TYPE_ICONS.empty;

  return (
    <div
      className="absolute cursor-pointer select-none"
      style={{
        left: screenX - width / 2,
        top: screenY - height / 2,
        width,
        height,
        transform: `rotate(${rotation}deg)`,
        transformOrigin: 'center',
        border: isSelected
          ? '2px solid rgba(59, 130, 246, 1)'
          : `1px solid ${color}`,
        borderRadius: 4,
        backgroundColor: isSelected ? `${color}40` : `${color}20`,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClick}
    >
      <TypeIcon size={Math.min(16, width * 0.4)} style={{ color, opacity: 0.7 }} />
      <PrefabObjectLabel name={obj.name} isSelected={isSelected} rotation={rotation} />
    </div>
  );
};

// ============================================================================
// PrefabScenePreview Component — HTML/CSS based preview (replaces PixiJS)
// ============================================================================

interface PrefabScenePreviewProps {
  instanceId: string;
  gameObjects: Record<string, PrefabGameObject>;
  rootId: string | null;
  selectedIds: string[];
  onSelectObject: (objectId: string, multiSelect: boolean) => void;
}

interface Renderable {
  id: string;
  obj: PrefabGameObject;
  worldX: number;
  worldY: number;
  worldRotation: number;
  worldScaleX: number;
  worldScaleY: number;
}

const PrefabScenePreview: React.FC<PrefabScenePreviewProps> = ({
  instanceId,
  gameObjects,
  rootId,
  selectedIds,
  onSelectObject,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Viewport state
  const [viewport, setViewport] = useState({ panX: 0, panY: 0, zoom: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Track container size
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // Collect renderables by walking the object tree
  const collectRenderables = useCallback((): Renderable[] => {
    const result: Renderable[] = [];

    const walk = (
      objectId: string,
      parentWorldX: number,
      parentWorldY: number,
      parentWorldRotation: number,
      parentWorldScaleX: number,
      parentWorldScaleY: number
    ) => {
      const obj = gameObjects[objectId];
      if (!obj) return;

      const rotationRad = (parentWorldRotation * Math.PI) / 180;
      const cos = Math.cos(rotationRad);
      const sin = Math.sin(rotationRad);

      const localX = obj.transform.position.x * parentWorldScaleX;
      const localY = obj.transform.position.y * parentWorldScaleY;

      const worldX = parentWorldX + (localX * cos - localY * sin);
      const worldY = parentWorldY + (localX * sin + localY * cos);
      const worldRotation = parentWorldRotation + obj.transform.rotation;
      const worldScaleX = parentWorldScaleX * obj.transform.scale.x;
      const worldScaleY = parentWorldScaleY * obj.transform.scale.y;

      result.push({ id: objectId, obj, worldX, worldY, worldRotation, worldScaleX, worldScaleY });

      for (const childId of obj.children) {
        walk(childId, worldX, worldY, worldRotation, worldScaleX, worldScaleY);
      }
    };

    if (rootId && gameObjects[rootId]) {
      walk(rootId, 0, 0, 0, 1, 1);
    }

    return result;
  }, [gameObjects, rootId]);

  // Handle wheel for zooming
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoomDelta = e.deltaY > 0 ? -0.1 : 0.1;
      setViewport((v) => ({
        ...v,
        zoom: Math.max(0.25, Math.min(4, v.zoom + zoomDelta * v.zoom)),
      }));
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  // Handle panning
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      e.preventDefault();
      setIsPanning(true);
      panStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        panX: viewport.panX,
        panY: viewport.panY,
      };
    }
  }, [viewport.panX, viewport.panY]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPanning && panStartRef.current) {
      const deltaX = (e.clientX - panStartRef.current.x) / viewport.zoom;
      const deltaY = (e.clientY - panStartRef.current.y) / viewport.zoom;
      setViewport((v) => ({
        ...v,
        panX: panStartRef.current!.panX + deltaX,
        panY: panStartRef.current!.panY + deltaY,
      }));
    }
  }, [isPanning, viewport.zoom]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    panStartRef.current = null;
  }, []);

  const handleResetView = useCallback(() => {
    setViewport({ panX: 0, panY: 0, zoom: 1 });
  }, []);

  const handleZoomIn = useCallback(() => {
    setViewport((v) => ({ ...v, zoom: Math.min(4, v.zoom * 1.25) }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setViewport((v) => ({ ...v, zoom: Math.max(0.25, v.zoom / 1.25) }));
  }, []);

  const handleBackgroundClick = useCallback(() => {
    // Clear selection when clicking on empty space
    onSelectObject('', false);
  }, [onSelectObject]);

  // Compute renderables and screen positions
  const renderables = collectRenderables();
  const { zoom, panX, panY } = viewport;
  const centerX = containerSize.width / 2;
  const centerY = containerSize.height / 2;

  return (
    <div className="relative w-full h-full flex flex-col">
      {/* Preview toolbar */}
      <div className="flex items-center gap-1 px-2 py-1 border-b border-zinc-700 bg-zinc-800/50">
        <button
          className="p-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200"
          onClick={handleResetView}
          title="Reset View (Home)"
        >
          <Home size={14} />
        </button>
        <button
          className="p-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200"
          onClick={handleZoomIn}
          title="Zoom In"
        >
          <ZoomIn size={14} />
        </button>
        <button
          className="p-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200"
          onClick={handleZoomOut}
          title="Zoom Out"
        >
          <ZoomOut size={14} />
        </button>
        <span className="text-xs text-zinc-500 ml-2">
          {Math.round(zoom * 100)}%
        </span>
        <span className="text-xs text-zinc-600 ml-auto">
          Alt+Drag or Middle-click to pan
        </span>
      </div>

      {/* Scene container */}
      <div
        ref={containerRef}
        className={cn(
          'flex-1 overflow-hidden relative bg-zinc-900',
          isPanning && 'cursor-grabbing'
        )}
        onMouseDown={(e) => {
          handleMouseDown(e);
          // Click on background to deselect
          if (e.target === e.currentTarget) {
            handleBackgroundClick();
          }
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Grid lines (CSS background) */}
        <div
          className="absolute pointer-events-none"
          style={{
            left: centerX + panX * zoom,
            top: centerY + panY * zoom,
            width: 1000 * zoom,
            height: 1000 * zoom,
            transform: 'translate(-50%, -50%)',
            backgroundImage:
              `linear-gradient(to right, rgba(39,39,42,0.5) 1px, transparent 1px),
               linear-gradient(to bottom, rgba(39,39,42,0.5) 1px, transparent 1px)`,
            backgroundSize: `${50 * zoom}px ${50 * zoom}px`,
          }}
        />

        {/* Origin crosshair */}
        <div
          className="absolute pointer-events-none"
          style={{
            left: centerX + panX * zoom - 1,
            top: centerY + panY * zoom - 20 * zoom,
            width: 2,
            height: 40 * zoom,
            backgroundColor: 'rgba(63, 63, 70, 0.8)',
          }}
        />
        <div
          className="absolute pointer-events-none"
          style={{
            left: centerX + panX * zoom - 20 * zoom,
            top: centerY + panY * zoom - 1,
            width: 40 * zoom,
            height: 2,
            backgroundColor: 'rgba(63, 63, 70, 0.8)',
          }}
        />

        {/* Rendered game objects */}
        {renderables.map((r) => {
          const screenX = centerX + (r.worldX + panX) * zoom;
          const screenY = centerY + (r.worldY + panY) * zoom;

          return (
            <PrefabGameObjectRenderer
              key={r.id}
              obj={r.obj}
              screenX={screenX}
              screenY={screenY}
              rotation={r.worldRotation}
              scaleX={r.worldScaleX}
              scaleY={r.worldScaleY}
              zoom={zoom}
              isSelected={selectedIds.includes(r.id)}
              onClick={(e) => {
                e.stopPropagation();
                onSelectObject(r.id, e.ctrlKey || e.metaKey);
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

const TreeNode: React.FC<TreeNodeProps> = ({
  objectId,
  instanceId,
  depth,
  onContextMenu,
  onStartRename,
  renamingId,
  onFinishRename,
}) => {
  const {
    prefabStates,
    selectPrefabObjects,
    togglePrefabExpanded,
    updatePrefabObject,
  } = useAssetPreviewStore();

  const prefabState = prefabStates[instanceId];
  const obj = prefabState?.gameObjects[objectId];

  const [renameValue, setRenameValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isRenaming = renamingId === objectId;

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      setRenameValue(obj?.name || '');
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming, obj?.name]);

  if (!obj || !prefabState) return null;

  const isSelected = prefabState.selectedIds.includes(objectId);
  const isExpanded = prefabState.expandedIds.includes(objectId);
  const hasChildren = obj.children.length > 0;
  const isRoot = obj.parentId === null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    selectPrefabObjects(instanceId, [objectId], e.ctrlKey || e.metaKey);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    togglePrefabExpanded(instanceId, objectId);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onStartRename(objectId);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Select this object if not already selected
    if (!prefabState.selectedIds.includes(objectId)) {
      selectPrefabObjects(instanceId, [objectId], false);
    }
    onContextMenu(e, objectId);
  };

  const handleRenameSubmit = () => {
    if (renameValue.trim() && renameValue !== obj.name) {
      updatePrefabObject(instanceId, objectId, { name: renameValue.trim() });
    }
    onFinishRename();
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      onFinishRename();
    }
  };

  const IconComponent = OBJECT_TYPE_ICONS[obj.type] || OBJECT_TYPE_ICONS.empty;

  return (
    <div>
      <div
        className={cn(
          'flex items-center h-7 px-1 cursor-pointer',
          'hover:bg-zinc-700/50',
          isSelected && 'bg-blue-600/30'
        )}
        style={{ paddingLeft: depth * 16 }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
      >
        {/* Expand/collapse toggle */}
        <button
          className={cn(
            'w-5 h-5 flex items-center justify-center',
            'text-zinc-500 hover:text-zinc-300',
            !hasChildren && 'invisible'
          )}
          onClick={handleToggle}
        >
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        {/* Icon */}
        <IconComponent size={14} className={cn('mr-1.5', isRoot ? 'text-green-400' : 'text-zinc-400')} />

        {/* Name */}
        {isRenaming ? (
          <input
            ref={inputRef}
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={handleRenameKeyDown}
            className="flex-1 bg-zinc-800 border border-blue-500 rounded px-1 text-sm text-zinc-200 outline-none"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className={cn('text-sm truncate', isSelected ? 'text-white' : 'text-zinc-300')}>
            {obj.name}
          </span>
        )}
      </div>

      {/* Children */}
      {isExpanded &&
        obj.children.map((childId) => (
          <TreeNode
            key={childId}
            objectId={childId}
            instanceId={instanceId}
            depth={depth + 1}
            onContextMenu={onContextMenu}
            onStartRename={onStartRename}
            renamingId={renamingId}
            onFinishRename={onFinishRename}
          />
        ))}
    </div>
  );
};

export const PrefabPreview: React.FC<PrefabPreviewProps> = ({ instanceId }) => {
  const {
    previews,
    prefabStates,
    initPrefabState,
    addPrefabChild,
    deletePrefabObject,
    undoPrefab,
    redoPrefab,
    copyPrefabObject,
    pastePrefabObject,
    getPrefabJSON,
    setDirty,
    selectPrefabObjects,
    updatePrefabObject,
    updatePrefabComponentData,
  } = useAssetPreviewStore();
  const { assets } = useProjectPanelStore();

  const instance = previews[instanceId];
  const asset = instance ? assets[instance.assetId] : null;
  const prefabState = prefabStates[instanceId];

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; targetId: string | null } | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);

  // Load prefab content
  useEffect(() => {
    if (!instance?.assetPath) return;

    const loadPrefab = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const content = await readFile(instance.assetPath);
        initPrefabState(instanceId, content);
      } catch (err) {
        console.error('Failed to load prefab:', err);
        setLoadError(err instanceof Error ? err.message : 'Failed to load prefab');
        // Initialize with empty prefab
        initPrefabState(instanceId, '{}');
      } finally {
        setIsLoading(false);
      }
    };

    loadPrefab();
  }, [instanceId, instance?.assetPath, initPrefabState]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if user is typing in an input or editor
      const activeElement = document.activeElement;
      if (
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.getAttribute('contenteditable') === 'true' ||
        activeElement?.closest('.monaco-editor') ||
        activeElement?.closest('[data-script-editor]')
      ) {
        return;
      }

      const isCtrl = e.ctrlKey || e.metaKey;

      if (isCtrl && e.key === 's') {
        e.preventDefault();
        handleSave();
      } else if (isCtrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undoPrefab(instanceId);
      } else if ((isCtrl && e.key === 'y') || (isCtrl && e.shiftKey && e.key === 'z')) {
        e.preventDefault();
        redoPrefab(instanceId);
      } else if (isCtrl && e.key === 'c') {
        e.preventDefault();
        copyPrefabObject(instanceId);
      } else if (isCtrl && e.key === 'v') {
        e.preventDefault();
        const parentId = prefabState?.selectedIds[0] || null;
        pastePrefabObject(instanceId, parentId);
      } else if (e.key === 'Delete' && prefabState?.selectedIds.length) {
        e.preventDefault();
        prefabState.selectedIds.forEach((id) => {
          if (prefabState.gameObjects[id]?.parentId !== null) {
            deletePrefabObject(instanceId, id);
          }
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [instanceId, prefabState, undoPrefab, redoPrefab, copyPrefabObject, pastePrefabObject, deletePrefabObject]);

  const handleSave = useCallback(async () => {
    if (!instance?.assetPath) return;

    setIsSaving(true);
    try {
      const json = getPrefabJSON(instanceId);
      await writeFile(instance.assetPath, json);
      setDirty(instanceId, false);
      console.log(`Saved prefab: ${instance.assetPath}`);
    } catch (err) {
      console.error('Failed to save prefab:', err);
    } finally {
      setIsSaving(false);
    }
  }, [instanceId, instance?.assetPath, getPrefabJSON, setDirty]);

  const handleAddChild = useCallback(
    (type: string) => {
      const parentId = prefabState?.selectedIds[0] || null;
      addPrefabChild(instanceId, parentId, type);
      setShowAddMenu(false);
    },
    [instanceId, prefabState?.selectedIds, addPrefabChild]
  );

  const handleDelete = useCallback(() => {
    if (!prefabState?.selectedIds.length) return;

    prefabState.selectedIds.forEach((id) => {
      const obj = prefabState.gameObjects[id];
      if (obj && obj.parentId !== null) {
        deletePrefabObject(instanceId, id);
      }
    });
  }, [instanceId, prefabState, deletePrefabObject]);

  // Memoize the onSelectObject callback to prevent unnecessary re-renders
  const handleSelectObject = useCallback(
    (objectId: string, multiSelect: boolean) => {
      selectPrefabObjects(instanceId, [objectId], multiSelect);
    },
    [instanceId, selectPrefabObjects]
  );

  // Context menu handlers
  const handleContextMenu = useCallback((e: React.MouseEvent, objectId: string) => {
    setContextMenu({ x: e.clientX, y: e.clientY, targetId: objectId });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleStartRename = useCallback((objectId: string) => {
    setRenamingId(objectId);
  }, []);

  const handleFinishRename = useCallback(() => {
    setRenamingId(null);
  }, []);

  if (!instance || !asset) {
    return (
      <div className="flex-1 flex items-center justify-center text-zinc-500">
        Asset not found
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="w-8 h-8 border-2 border-zinc-600 border-t-green-500 rounded-full animate-spin mb-2" />
        <div className="text-zinc-500 text-sm">Loading prefab...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
        <div className="text-4xl mb-2">⚠️</div>
        <div className="text-zinc-400 text-sm">Failed to load prefab</div>
        <div className="text-zinc-600 text-xs mt-1">{loadError}</div>
      </div>
    );
  }

  const canUndo = (prefabState?.undoStack.length ?? 0) > 0;
  const canRedo = (prefabState?.redoStack.length ?? 0) > 0;
  const canDelete = prefabState?.selectedIds.some(
    (id) => prefabState.gameObjects[id]?.parentId !== null
  );
  const hasClipboard = prefabState?.clipboardData !== null;

  const selectedObject = prefabState?.selectedIds.length === 1
    ? prefabState.gameObjects[prefabState.selectedIds[0]]
    : null;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-zinc-700 bg-zinc-800/50">
        <button
          className={cn(
            'p-1.5 rounded',
            'hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          onClick={() => undoPrefab(instanceId)}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
        >
          <Undo size={16} />
        </button>
        <button
          className={cn(
            'p-1.5 rounded',
            'hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          onClick={() => redoPrefab(instanceId)}
          disabled={!canRedo}
          title="Redo (Ctrl+Y)"
        >
          <Redo size={16} />
        </button>

        <div className="w-px h-4 bg-zinc-700 mx-1" />

        <div className="relative">
          <button
            className={cn(
              'p-1.5 rounded',
              'hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200',
              showAddMenu && 'bg-zinc-700'
            )}
            onClick={() => setShowAddMenu(!showAddMenu)}
            title="Add Child Object"
          >
            <Plus size={16} />
          </button>

          {showAddMenu && (
            <div className="absolute top-full left-0 mt-1 bg-zinc-800 border border-zinc-700 rounded shadow-lg z-10 py-1 min-w-[120px]">
              <button
                className="w-full px-3 py-1.5 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                onClick={() => handleAddChild('empty')}
              >
                <Box size={14} /> Empty
              </button>
              <button
                className="w-full px-3 py-1.5 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                onClick={() => handleAddChild('sprite')}
              >
                <Square size={14} /> Sprite
              </button>
              <button
                className="w-full px-3 py-1.5 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                onClick={() => handleAddChild('text')}
              >
                <Type size={14} /> Text
              </button>
              <button
                className="w-full px-3 py-1.5 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                onClick={() => handleAddChild('container')}
              >
                <Box size={14} /> Container
              </button>
            </div>
          )}
        </div>

        <button
          className={cn(
            'p-1.5 rounded',
            'hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          onClick={handleDelete}
          disabled={!canDelete}
          title="Delete (Del)"
        >
          <Trash2 size={16} />
        </button>

        <div className="w-px h-4 bg-zinc-700 mx-1" />

        <button
          className={cn(
            'p-1.5 rounded',
            'hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          onClick={() => copyPrefabObject(instanceId)}
          disabled={!prefabState?.selectedIds.length}
          title="Copy (Ctrl+C)"
        >
          <Copy size={16} />
        </button>
        <button
          className={cn(
            'p-1.5 rounded',
            'hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          onClick={() => pastePrefabObject(instanceId, prefabState?.selectedIds[0] || null)}
          disabled={!hasClipboard}
          title="Paste (Ctrl+V)"
        >
          <Clipboard size={16} />
        </button>

        <div className="flex-1" />

        <button
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded text-sm',
            instance.isDirty
              ? 'bg-blue-600 hover:bg-blue-500 text-white'
              : 'bg-zinc-700 hover:bg-zinc-600 text-zinc-300',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          onClick={handleSave}
          disabled={isSaving || !instance.isDirty}
          title="Save (Ctrl+S)"
        >
          <Save size={14} />
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* Close menu on click outside */}
      {showAddMenu && (
        <div className="fixed inset-0 z-0" onClick={() => setShowAddMenu(false)} />
      )}

      {/* Main content - three-column split view */}
      <div className="flex-1 flex overflow-hidden">
        {/* Scene Preview (PixiJS canvas) */}
        <div className="w-1/3 border-r border-zinc-700 overflow-hidden">
          {prefabState && (
            <PrefabScenePreview
              instanceId={instanceId}
              gameObjects={prefabState.gameObjects}
              rootId={prefabState.rootId}
              selectedIds={prefabState.selectedIds}
              onSelectObject={handleSelectObject}
            />
          )}
        </div>

        {/* Hierarchy tree */}
        <div className="w-1/3 border-r border-zinc-700 overflow-auto">
          <div className="py-1">
            {prefabState?.rootId && (
              <TreeNode
                objectId={prefabState.rootId}
                instanceId={instanceId}
                depth={0}
                onContextMenu={handleContextMenu}
                onStartRename={handleStartRename}
                renamingId={renamingId}
                onFinishRename={handleFinishRename}
              />
            )}
          </div>
        </div>

        {/* Context Menu */}
        {contextMenu && (
          <PrefabContextMenu
            targetId={contextMenu.targetId}
            instanceId={instanceId}
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={handleCloseContextMenu}
            onRename={handleStartRename}
          />
        )}

        {/* Properties panel */}
        <div className="w-1/3 overflow-auto p-3">
          {selectedObject ? (
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Name</label>
                <input
                  type="text"
                  value={selectedObject.name}
                  onChange={(e) =>
                    updatePrefabObject(instanceId, selectedObject.id, { name: e.target.value })
                  }
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-200 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs text-zinc-500 mb-1">Position</label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-zinc-600 mb-0.5">X</label>
                    <input
                      type="number"
                      value={selectedObject.transform.position.x}
                      onChange={(e) =>
                        updatePrefabObject(instanceId, selectedObject.id, {
                          transform: {
                            ...selectedObject.transform,
                            position: {
                              ...selectedObject.transform.position,
                              x: parseFloat(e.target.value) || 0,
                            },
                          },
                        })
                      }
                      className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-200 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-zinc-600 mb-0.5">Y</label>
                    <input
                      type="number"
                      value={selectedObject.transform.position.y}
                      onChange={(e) =>
                        updatePrefabObject(instanceId, selectedObject.id, {
                          transform: {
                            ...selectedObject.transform,
                            position: {
                              ...selectedObject.transform.position,
                              y: parseFloat(e.target.value) || 0,
                            },
                          },
                        })
                      }
                      className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-200 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs text-zinc-500 mb-1">Rotation</label>
                <input
                  type="number"
                  value={selectedObject.transform.rotation}
                  onChange={(e) =>
                    updatePrefabObject(instanceId, selectedObject.id, {
                      transform: {
                        ...selectedObject.transform,
                        rotation: parseFloat(e.target.value) || 0,
                      },
                    })
                  }
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-200 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs text-zinc-500 mb-1">Scale</label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-zinc-600 mb-0.5">X</label>
                    <input
                      type="number"
                      step="0.1"
                      value={selectedObject.transform.scale.x}
                      onChange={(e) =>
                        updatePrefabObject(instanceId, selectedObject.id, {
                          transform: {
                            ...selectedObject.transform,
                            scale: {
                              ...selectedObject.transform.scale,
                              x: parseFloat(e.target.value) || 1,
                            },
                          },
                        })
                      }
                      className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-200 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-zinc-600 mb-0.5">Y</label>
                    <input
                      type="number"
                      step="0.1"
                      value={selectedObject.transform.scale.y}
                      onChange={(e) =>
                        updatePrefabObject(instanceId, selectedObject.id, {
                          transform: {
                            ...selectedObject.transform,
                            scale: {
                              ...selectedObject.transform.scale,
                              y: parseFloat(e.target.value) || 1,
                            },
                          },
                        })
                      }
                      className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-200 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Component sections */}
              {selectedObject.components.map((comp, compIndex) => {
                const compType = (comp as { type?: string }).type;
                if (!compType) return null;
                const definition = getComponentDefinition(compType);
                if (!definition) return null;
                const compData = (comp as { data?: Record<string, unknown> }).data || {};

                const handleCompPropChange = (propertyName: string, value: unknown) => {
                  updatePrefabComponentData(instanceId, selectedObject.id, compType, propertyName, value);
                };

                const renderCompProperty = (property: PropertyDefinition) => {
                  if (property.hidden) return null;
                  const value = compData[property.name] ?? property.defaultValue;

                  switch (property.type) {
                    case 'texture':
                      return (
                        <div key={property.name} className="space-y-1">
                          <label className="text-xs text-zinc-500">{property.label}</label>
                          <TexturePropertyEditor
                            value={
                              (compData.texturePath as string | null) ??
                              (value as string | null) ??
                              null
                            }
                            onChange={(path) => {
                              handleCompPropChange('textureId', path);
                              handleCompPropChange('texturePath', path);
                            }}
                            onTextureMeta={(w, h) => {
                              handleCompPropChange('textureWidth', w);
                              handleCompPropChange('textureHeight', h);
                            }}
                          />
                        </div>
                      );

                    case 'color':
                      return (
                        <div key={property.name}>
                          <ColorPicker
                            value={value as string ?? property.defaultValue ?? '#ffffff'}
                            onChange={(v) => handleCompPropChange(property.name, v)}
                            label={property.label}
                          />
                        </div>
                      );

                    case 'slider':
                      return (
                        <div key={property.name}>
                          <SliderInput
                            value={value as number ?? property.defaultValue ?? 0}
                            onChange={(v) => handleCompPropChange(property.name, v)}
                            label={property.label}
                            min={property.min ?? 0}
                            max={property.max ?? 1}
                            step={property.step ?? 0.01}
                          />
                        </div>
                      );

                    case 'boolean':
                      return (
                        <div key={property.name} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={value as boolean ?? false}
                            onChange={(e) => handleCompPropChange(property.name, e.target.checked)}
                            className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                          />
                          <label className="text-xs text-zinc-400">{property.label}</label>
                        </div>
                      );

                    case 'dropdown':
                      return (
                        <div key={property.name} className="space-y-1">
                          <label className="text-xs text-zinc-500">{property.label}</label>
                          <select
                            value={value as string ?? ''}
                            onChange={(e) => handleCompPropChange(property.name, e.target.value)}
                            className="w-full px-2 py-1.5 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-300 focus:outline-none focus:border-blue-500 appearance-none cursor-pointer"
                          >
                            {property.options?.map((opt) => {
                              const optVal = typeof opt === 'string' ? opt : opt.value;
                              const optLabel = typeof opt === 'string' ? opt : opt.label;
                              return (
                                <option key={optVal} value={optVal}>{optLabel}</option>
                              );
                            })}
                          </select>
                        </div>
                      );

                    case 'number':
                      return (
                        <div key={property.name} className="space-y-1">
                          <label className="text-xs text-zinc-500">{property.label}</label>
                          <input
                            type="number"
                            value={value as number ?? 0}
                            onChange={(e) => handleCompPropChange(property.name, parseFloat(e.target.value) || 0)}
                            min={property.min}
                            max={property.max}
                            step={property.step}
                            className="w-full px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-300 focus:outline-none focus:border-blue-500"
                          />
                        </div>
                      );

                    case 'string':
                      return (
                        <div key={property.name} className="space-y-1">
                          <label className="text-xs text-zinc-500">{property.label}</label>
                          <input
                            type="text"
                            value={value as string ?? ''}
                            onChange={(e) => handleCompPropChange(property.name, e.target.value)}
                            className="w-full px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded text-zinc-300 focus:outline-none focus:border-blue-500"
                          />
                        </div>
                      );

                    default:
                      return null;
                  }
                };

                return (
                  <div key={`${compType}-${compIndex}`} className="border-t border-zinc-700 pt-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="text-sm">{definition.icon}</span>
                      <span className="text-xs font-medium text-zinc-300">{definition.name}</span>
                    </div>
                    <div className="space-y-2">
                      {definition.properties.map(renderCompProperty)}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
              Select an object to view properties
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
